import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  CheckCircle2,
  ClipboardList,
  Download,
  Loader2,
  Pencil,
  Sparkles,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const UF_OPTIONS = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC",
  "SP", "SE", "TO",
];

const MARITAL_STATUS_OPTIONS = [
  { value: "solteiro", label: "Solteiro(a)" },
  { value: "casado", label: "Casado(a)" },
  { value: "divorciado", label: "Divorciado(a)" },
  { value: "viuvo", label: "Viúvo(a)" },
  { value: "uniao_estavel", label: "União estável" },
  { value: "outro", label: "Outro" },
];

const SEX_OPTIONS = [
  { value: "masculino", label: "Masculino" },
  { value: "feminino", label: "Feminino" },
  { value: "outro", label: "Outro" },
];

const EMPTY_FORM = {
  cpf: "",
  sex: "",
  rg: "",
  rg_issuing_body: "",
  rg_issue_date: "",
  marital_status: "",
  birth_date: "",
  cnh_number: "",
  cnh_category: "",
  cnh_issue_date: "",
  birthplace: "",
  birthplace_uf: "",
  nationality: "",
  father_name: "",
  mother_name: "",
  address: "",
};

const FIELD_LABELS = {
  cpf: "CPF",
  sex: "Sexo",
  rg: "RG",
  rg_issuing_body: "Órgão emissor",
  rg_issue_date: "Data de expedição (RG)",
  marital_status: "Estado civil",
  birth_date: "Data de nascimento",
  cnh_number: "CNH",
  cnh_category: "Categoria CNH",
  cnh_issue_date: "Data de expedição (CNH)",
  birthplace: "Naturalidade",
  birthplace_uf: "UF",
  nationality: "Nacionalidade",
  father_name: "Nome do pai",
  mother_name: "Nome da mãe",
  address: "Endereço",
};

// Every field is required before saving except the father's name — some
// candidates don't have that information available.
const REQUIRED_FIELDS = Object.keys(EMPTY_FORM).filter(
  (f) => f !== "father_name",
);

const digitsOnly = (value) => (value || "").replace(/\D/g, "");

const inputClass =
  "w-full border border-slate-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30";
const labelClass =
  "text-[11px] uppercase tracking-wider text-slate-500 font-bold block mb-1";

const RequiredLabel = ({ field }) => (
  <label className={labelClass}>
    {FIELD_LABELS[field]}
    {REQUIRED_FIELDS.includes(field) && (
      <span className="text-red-500"> *</span>
    )}
  </label>
);

function displayValue(field, value) {
  const v = (value || "").trim();
  if (!v) return "—";
  if (field === "marital_status") {
    return MARITAL_STATUS_OPTIONS.find((o) => o.value === v)?.label || v;
  }
  if (field === "sex") {
    return SEX_OPTIONS.find((o) => o.value === v)?.label || v;
  }
  return v;
}

/**
 * CandidateAdditionalInfoDialog
 *
 * Form for an HR user to fill in personal/document data that isn't
 * extracted from the résumé (CPF, RG, CNH, filiação, naturalidade, etc.)
 * ahead of an interview. Once saved, switches to a summary view with
 * "Editar" and "Baixar PDF" actions side by side.
 *
 * Props:
 *   open: boolean
 *   onClose(): void
 *   candidateId: string
 *   onSaved(candidate): optional callback after a successful save
 */
const CandidateAdditionalInfoDialog = ({
  open,
  onClose,
  candidateId,
  onSaved,
}) => {
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(true);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current && candidateId) {
      setLoading(true);
      setCandidate(null);
      setForm(EMPTY_FORM);
      axios
        .get(`${API}/candidates/${candidateId}`)
        .then((res) => {
          const c = res.data;
          setCandidate(c);
          const ai = c.additional_info || {};
          const merged = { ...EMPTY_FORM, ...ai };
          setForm(merged);
          const hasData = Object.keys(EMPTY_FORM).some((k) =>
            (merged[k] || "").trim(),
          );
          setEditing(!hasData);
        })
        .catch(() =>
          toast.error("Não consegui carregar os dados do candidato."),
        )
        .finally(() => setLoading(false));
    }
    wasOpenRef.current = open;
  }, [open, candidateId]);

  const setField = useCallback((field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  }, []);

  const missingFields = REQUIRED_FIELDS.filter((f) => !(form[f] || "").trim());
  const canSave = missingFields.length === 0;

  const handleSave = useCallback(async () => {
    if (!candidateId || !canSave) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API}/candidates/${candidateId}/additional-info`,
        form,
      );
      setForm({ ...EMPTY_FORM, ...(res.data.additional_info || {}) });
      setCandidate(res.data);
      setEditing(false);
      toast.success("Dados complementares salvos!");
      onSaved?.(res.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Não consegui salvar os dados.",
      );
    } finally {
      setSaving(false);
    }
  }, [candidateId, canSave, form, onSaved]);

  const handleDownloadPdf = useCallback(async () => {
    if (!candidateId) return;
    setDownloading(true);
    try {
      const res = await axios.get(
        `${API}/candidates/${candidateId}/additional-info.pdf`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const safeName =
        (candidate?.name || "candidato")
          .normalize("NFD")
          .replace(/[̀-ͯ]/g, "")
          .replace(/[^A-Za-z0-9_-]+/g, "_")
          .slice(0, 60) || "candidato";
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dados_Complementares_${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success("Download iniciado");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Não consegui gerar o PDF.",
      );
    } finally {
      setDownloading(false);
    }
  }, [candidateId, candidate]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="candidate-additional-info-dialog"
        className="max-w-[620px] max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-brand-blue" />
            Completar dados do candidato
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
            Carregando dados do candidato…
          </div>
        ) : (
          <div className="space-y-5">
            {candidate && (
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-violet-500" />
                  Já extraído do currículo
                </span>
                <div className="font-semibold text-slate-900 mt-1">
                  {candidate.name || "(sem nome)"}
                </div>
                <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  {candidate.email && <span>{candidate.email}</span>}
                  {candidate.phone && <span>{candidate.phone}</span>}
                </div>
              </div>
            )}

            {!editing ? (
              <>
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                  <p className="text-sm text-emerald-900">
                    Dados complementares salvos.
                  </p>
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                  {Object.keys(FIELD_LABELS).map((field) => (
                    <div key={field} className={field === "address" ? "col-span-2" : ""}>
                      <dt className={labelClass}>{FIELD_LABELS[field]}</dt>
                      <dd className="text-slate-800">
                        {displayValue(field, form[field])}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                  <Button
                    onClick={() => setEditing(true)}
                    variant="outline"
                    size="sm"
                    data-testid="edit-additional-info"
                  >
                    <Pencil className="w-3.5 h-3.5 mr-1.5" />
                    Editar
                  </Button>
                  <Button
                    onClick={handleDownloadPdf}
                    disabled={downloading}
                    data-testid="download-additional-info-pdf"
                    size="sm"
                    className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                  >
                    {downloading ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    Baixar PDF
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <RequiredLabel field="cpf" />
                      <input
                        value={form.cpf}
                        onChange={(e) =>
                          setField("cpf", digitsOnly(e.target.value).slice(0, 11))
                        }
                        inputMode="numeric"
                        maxLength={11}
                        data-testid="field-cpf"
                        className={inputClass}
                        placeholder="Somente números"
                      />
                    </div>
                    <div>
                      <RequiredLabel field="sex" />
                      <Select
                        value={form.sex}
                        onValueChange={(v) => setField("sex", v)}
                      >
                        <SelectTrigger data-testid="field-sex">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {SEX_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <RequiredLabel field="rg" />
                      <input
                        value={form.rg}
                        onChange={(e) =>
                          setField("rg", digitsOnly(e.target.value).slice(0, 14))
                        }
                        inputMode="numeric"
                        data-testid="field-rg"
                        className={inputClass}
                        placeholder="Somente números"
                      />
                    </div>
                    <div>
                      <RequiredLabel field="rg_issuing_body" />
                      <input
                        value={form.rg_issuing_body}
                        onChange={(e) =>
                          setField("rg_issuing_body", e.target.value)
                        }
                        data-testid="field-rg-issuing-body"
                        className={inputClass}
                        placeholder="Ex: SSP-SP"
                      />
                    </div>

                    <div>
                      <RequiredLabel field="rg_issue_date" />
                      <input
                        type="date"
                        value={form.rg_issue_date}
                        onChange={(e) =>
                          setField("rg_issue_date", e.target.value)
                        }
                        data-testid="field-rg-issue-date"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <RequiredLabel field="marital_status" />
                      <Select
                        value={form.marital_status}
                        onValueChange={(v) => setField("marital_status", v)}
                      >
                        <SelectTrigger data-testid="field-marital-status">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {MARITAL_STATUS_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <RequiredLabel field="birth_date" />
                      <input
                        type="date"
                        value={form.birth_date}
                        onChange={(e) => setField("birth_date", e.target.value)}
                        data-testid="field-birth-date"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <RequiredLabel field="cnh_number" />
                      <input
                        value={form.cnh_number}
                        onChange={(e) => setField("cnh_number", e.target.value)}
                        data-testid="field-cnh-number"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <RequiredLabel field="cnh_category" />
                      <input
                        value={form.cnh_category}
                        onChange={(e) =>
                          setField("cnh_category", e.target.value)
                        }
                        data-testid="field-cnh-category"
                        className={inputClass}
                        placeholder="Ex: AB"
                      />
                    </div>
                    <div>
                      <RequiredLabel field="cnh_issue_date" />
                      <input
                        type="date"
                        value={form.cnh_issue_date}
                        onChange={(e) =>
                          setField("cnh_issue_date", e.target.value)
                        }
                        data-testid="field-cnh-issue-date"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <RequiredLabel field="birthplace" />
                      <input
                        value={form.birthplace}
                        onChange={(e) => setField("birthplace", e.target.value)}
                        data-testid="field-birthplace"
                        className={inputClass}
                        placeholder="Cidade de nascimento"
                      />
                    </div>
                    <div>
                      <RequiredLabel field="birthplace_uf" />
                      <Select
                        value={form.birthplace_uf}
                        onValueChange={(v) => setField("birthplace_uf", v)}
                      >
                        <SelectTrigger data-testid="field-birthplace-uf">
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {UF_OPTIONS.map((uf) => (
                            <SelectItem key={uf} value={uf}>
                              {uf}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <RequiredLabel field="nationality" />
                      <input
                        value={form.nationality}
                        onChange={(e) => setField("nationality", e.target.value)}
                        data-testid="field-nationality"
                        className={inputClass}
                        placeholder="Ex: Brasileira"
                      />
                    </div>
                    <div>
                      <RequiredLabel field="father_name" />
                      <input
                        value={form.father_name}
                        onChange={(e) => setField("father_name", e.target.value)}
                        data-testid="field-father-name"
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <RequiredLabel field="mother_name" />
                      <input
                        value={form.mother_name}
                        onChange={(e) => setField("mother_name", e.target.value)}
                        data-testid="field-mother-name"
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <RequiredLabel field="address" />
                    <input
                      value={form.address}
                      onChange={(e) => setField("address", e.target.value)}
                      data-testid="field-address"
                      className={inputClass}
                      placeholder="Rua, número, bairro, cidade - UF"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                  <p className="text-[11px] text-slate-500">
                    {canSave
                      ? "Todos os campos obrigatórios (*) foram preenchidos."
                      : `Faltam ${missingFields.length} campo(s) obrigatório(s) (*) para poder salvar e gerar o PDF.`}
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={
                        candidate?.additional_info &&
                        Object.keys(EMPTY_FORM).some((k) =>
                          (candidate.additional_info[k] || "").trim(),
                        )
                          ? () => setEditing(false)
                          : onClose
                      }
                      variant="outline"
                      size="sm"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSave}
                      disabled={saving || !canSave}
                      data-testid="save-additional-info"
                      size="sm"
                      className="bg-brand-blue hover:bg-brand-blue-dark text-white"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          Salvando…
                        </>
                      ) : (
                        <>
                          <Save className="w-3.5 h-3.5 mr-1.5" />
                          Salvar dados
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CandidateAdditionalInfoDialog;
